import {
    Grid,
    Paper,
    Table,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableBody,
    Chip,
    Typography,
} from '@mui/material';
import { t } from 'i18next';
import React, { useEffect } from 'react';
import Tooltip from '@mui/material/Tooltip';
import TimeHelper from '../../../utils/time';
import Swal from 'sweetalert2';
import { RpaQuery } from '../../../services/query';
import { RPRL } from './rpaRequestList.classes';

const COL_WIDTHS = {
    id: '120px',
    date: '200px',
    person: '250px',
    status: '200px',
} as const;

const headerCellSx = {
    borderBottom: 'none',
    textAlign: 'start' as const,
    py: 1.5,
    px: 2,
};

const bodyCellSx = {
    borderBottom: 'none',
    textAlign: 'start' as const,
    py: 1.5,
    px: 2,
};

function RpaRequestList() {
    const [datas, setDatas] = React.useState<any>();

    const fetchRpaRequestList = () => {
        const req = { take: 100, skip: 0 };
        RpaQuery.RpaRequestList(req)
            .then((res: any) => {
                if (res) {
                    setDatas(res.model.data);
                }
            })
            .catch((error: any) => {
                Swal.fire('', ' ', 'error');
                console.error('RpaRequestList error:', error);
            });
    };

    useEffect(() => {
        fetchRpaRequestList();
    }, []);

    return (
        <Grid
            className={RPRL.root}
            size={{
                sm: 12,
                md: 12,
                lg: 12,
                xl: 12,
            }}
        >
            {/* Header row */}
            <Paper
                className={RPRL.headerPaper}
                elevation={0}
                sx={{
                    mb: 1,
                    borderRadius: '12px',
                    border: '1px solid #e0e7ef',
                    backgroundColor: '#f4f6f9',
                    boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                    overflow: 'hidden',
                }}
            >
                <TableContainer className={RPRL.headerTableWrap}>
                    <Table className={RPRL.headerTable} aria-label="header table">
                        <TableHead className={RPRL.headerTableHead}>
                            <TableRow className={RPRL.headerRow}>
                                <TableCell
                                    className={RPRL.headerCellId}
                                    sx={{ ...headerCellSx, width: COL_WIDTHS.id, minWidth: COL_WIDTHS.id }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                    >
                                        ID
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    className={RPRL.headerCellDate}
                                    sx={{ ...headerCellSx, width: COL_WIDTHS.date, minWidth: COL_WIDTHS.date }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                    >
                                        {String(t('rpaResender.rpaRequestList.date'))}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    className={RPRL.headerCellPerson}
                                    sx={{ ...headerCellSx, width: COL_WIDTHS.person, minWidth: COL_WIDTHS.person }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                    >
                                        {String(t('rpaResender.rpaRequestList.person'))}
                                    </Typography>
                                </TableCell>
                                <TableCell
                                    className={RPRL.headerCellStatus}
                                    sx={{ ...headerCellSx, width: COL_WIDTHS.status, minWidth: COL_WIDTHS.status }}
                                >
                                    <Typography
                                        variant="caption"
                                        fontWeight={700}
                                        color="text.secondary"
                                        sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
                                    >
                                        {String(t('customerInformation.status'))}
                                    </Typography>
                                </TableCell>
                            </TableRow>
                        </TableHead>
                    </Table>
                </TableContainer>
            </Paper>

            {/* Data rows */}
            {datas && (
                <>
                    {datas.map((data: any) => (
                        <Paper
                            key={data.rpaRequestId}
                            className={RPRL.rowPaper}
                            elevation={0}
                            sx={{
                                mb: 1,
                                borderRadius: '12px',
                                border: '1px solid #e8eaed',
                                boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
                                overflow: 'hidden',
                                transition: 'box-shadow 0.2s ease, border-color 0.2s ease, transform 0.15s ease',
                                '&:hover': {
                                    boxShadow: '0 4px 16px rgba(0,0,0,0.1)',
                                    borderColor: '#b0bec5',
                                    backgroundColor: '#f4f6f9',
                                    transform: 'translateY(-1px)',
                                },
                            }}
                        >
                            <TableContainer className={RPRL.rowTableWrap}>
                                <Table className={RPRL.rowTable} aria-label="data row table">
                                    <TableHead>
                                        <TableRow />
                                    </TableHead>
                                    <TableBody className={RPRL.rowBody}>
                                        <TableRow className={RPRL.row} data-rpa-request-id={data.rpaRequestId}>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={RPRL.cellId}
                                                sx={{ ...bodyCellSx, width: COL_WIDTHS.id, minWidth: COL_WIDTHS.id }}
                                            >
                                                <Typography variant="body2" fontWeight={700} color="text.primary">
                                                    {data.rpaRequestId}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={RPRL.cellDate}
                                                sx={{
                                                    ...bodyCellSx,
                                                    width: COL_WIDTHS.date,
                                                    minWidth: COL_WIDTHS.date,
                                                }}
                                            >
                                                <Tooltip
                                                    className={RPRL.dateTooltip}
                                                    title={String(t('rpaResender.rpaRequestList.lastUpdateDate'))}
                                                    arrow
                                                >
                                                    <Typography
                                                        variant="body2"
                                                        color="text.secondary"
                                                        sx={{ fontVariantNumeric: 'tabular-nums' }}
                                                    >
                                                        {TimeHelper.formatDateHour(data.createDate)}
                                                    </Typography>
                                                </Tooltip>
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={RPRL.cellPerson}
                                                sx={{
                                                    ...bodyCellSx,
                                                    width: COL_WIDTHS.person,
                                                    minWidth: COL_WIDTHS.person,
                                                }}
                                            >
                                                <Typography variant="body2" fontWeight={500} color="text.primary">
                                                    {data.userName}
                                                </Typography>
                                            </TableCell>
                                            <TableCell
                                                component="th"
                                                scope="row"
                                                className={RPRL.cellStatus}
                                                sx={{
                                                    ...bodyCellSx,
                                                    width: COL_WIDTHS.status,
                                                    minWidth: COL_WIDTHS.status,
                                                }}
                                            >
                                                {data.statusKey === 'WaitingProcess' ? (
                                                    <Chip
                                                        className={RPRL.statusChipWaiting}
                                                        label={String(t('rpaResender.rpaRequestList.waitingProcess'))}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#fff3e0',
                                                            color: '#e65100',
                                                            fontWeight: 600,
                                                            border: '1px solid #ffcc80',
                                                            borderRadius: '8px',
                                                        }}
                                                    />
                                                ) : data.statusKey === 'Completed' ? (
                                                    <Chip
                                                        className={RPRL.statusChipCompleted}
                                                        label={String(t('rpaResender.rpaRequestList.completed'))}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#e8f5e9',
                                                            color: '#2e7d32',
                                                            fontWeight: 600,
                                                            border: '1px solid #a5d6a7',
                                                            borderRadius: '8px',
                                                        }}
                                                    />
                                                ) : (
                                                    <Chip
                                                        className={RPRL.statusChipOther}
                                                        label={data.statusKey}
                                                        size="small"
                                                        sx={{
                                                            backgroundColor: '#f5f5f5',
                                                            color: '#546e7a',
                                                            fontWeight: 600,
                                                            border: '1px solid #e0e0e0',
                                                            borderRadius: '8px',
                                                        }}
                                                    />
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    ))}
                </>
            )}
        </Grid>
    );
}
export default RpaRequestList;
